import { DateTime as LuxonDateTime } from "luxon";

import { Weekday } from "@/data/model/Enums";

import { Misc } from "./Misc";

export class DateTime {
    static parseIsoDate(dataString: string): Date {
        if (dataString.substr(dataString.length - 1, 1) !== "Z" && dataString.substr(dataString.length - 6, 1) !== "+")
            dataString += "Z";

        return new Date(dataString);
    }

    static convertToUTC(date) {
        return new Date(
            Date.UTC(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate(),
                date.getUTCHours(),
                date.getUTCMinutes(),
                date.getUTCSeconds()
            )
        );
    }

    static serializeIsoDate(date: Date): string {
        return (
            date.getFullYear() +
            "-" +
            Misc.padDigits(date.getMonth() + 1, 2) +
            "-" +
            Misc.padDigits(date.getDate(), 2) +
            "T" +
            Misc.padDigits(date.getHours(), 2) +
            ":" +
            Misc.padDigits(date.getMinutes(), 2) +
            ":" +
            Misc.padDigits(date.getSeconds(), 2) +
            "+" +
            Misc.padDigits(Math.round(date.getTimezoneOffset() / -60), 2) +
            ":00"
        );
    }

    // TODO: To improve
    static toDate(time: string): Date {
        const [hours, minutes] = time.split(":").map(Number);

        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(0);

        return date;
    }

    static toDateString(date: Date, useUtc?: boolean): string {
        if (useUtc !== undefined && useUtc === true)
            return `${Misc.padDigits(date.getUTCDate(), 2)}.${Misc.padDigits(
                date.getUTCMonth() + 1,
                2
            )}.${date.getUTCFullYear()}`;

        return `${Misc.padDigits(date.getDate(), 2)}.${Misc.padDigits(date.getMonth() + 1, 2)}.${date.getFullYear()}`;
    }

    static toTimeString(date: Date, withSeconds?: boolean, useUtc?: boolean): string {
        if (useUtc !== undefined && useUtc === true) {
            let b = `${Misc.padDigits(date.getUTCHours(), 2)}:${Misc.padDigits(date.getUTCMinutes(), 2)}`;

            if (withSeconds !== undefined && withSeconds === true) b += `:${Misc.padDigits(date.getUTCSeconds(), 2)}`;

            return b;
        } else {
            let b = `${Misc.padDigits(date.getHours(), 2)}:${Misc.padDigits(date.getMinutes(), 2)}`;

            if (withSeconds !== undefined && withSeconds === true) b += `:${Misc.padDigits(date.getSeconds(), 2)}`;

            return b;
        }
    }

    static toDayOfMonthString(date: Date, useUtc?: boolean): string {
        if (useUtc !== undefined && useUtc === true)
            return `${Misc.padDigits(date.getUTCDate(), 2)}.${Misc.padDigits(date.getUTCMonth() + 1, 2)}`;

        return `${Misc.padDigits(date.getDate(), 2)}.${Misc.padDigits(date.getMonth() + 1, 2)}`;
    }

    static toMonthString(date: Date, useUtc?: boolean): string {
        if (useUtc !== undefined && useUtc === true)
            return `${Misc.padDigits(date.getUTCMonth() + 1, 2)}.${date.getUTCFullYear()}`;

        return `${Misc.padDigits(date.getMonth() + 1, 2)}.${date.getFullYear()}`;
    }

    static toDateTimeString(date: Date): string {
        return `${DateTime.toDateString(date)} ${DateTime.toTimeString(date)}`;
    }

    static toPreciseDateTimeString(date: Date): string {
        return `${DateTime.toDateString(date)} ${DateTime.toTimeString(date)}:${Misc.padDigits(date.getSeconds(), 2)}`;
    }

    static timeSpanTotime(timespan: string): string {
        const splits = timespan.split(":");
        if (splits.length !== 3) return "";

        return splits[0] + ":" + splits[1];
    }

    static secondsToTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const leftOverSeconds = seconds - hours * 3600;
        if (leftOverSeconds >= 3599 && hours === 23) return "24:00";

        const minutes = Math.floor(leftOverSeconds / 60);
        return Misc.padDigits(hours, 2) + ":" + Misc.padDigits(minutes, 2);
    }

    static secondsOfTimeSpan(timespan: string): number {
        if (timespan === undefined) return 0;

        const splits = timespan.split(":");
        if (splits.length !== 3) return 0;

        return parseInt(splits[0]) * 3600 + parseInt(splits[1]) * 60 + parseInt(splits[2]);
    }

    static secondsToTimespan(seconds: number): string {
        const isNegative = seconds < 0;

        seconds = Math.abs(seconds);
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds - hours * 3600) / 60);
        const s = seconds - hours * 3600 - minutes * 60;

        return (
            (isNegative ? "-" : "") +
            Misc.padDigits(hours, 2) +
            ":" +
            Misc.padDigits(minutes, 2) +
            ":" +
            Misc.padDigits(s, 2)
        );
    }

    static secondsSinceMidnight(date: Date): number {
        return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
    }

    static overlaps(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
        if (b.start < a.start) return b.end > a.start;
        else return b.start < a.end;
    }

    static addTimeOffset(date: Date): Date {
        return new Date(date.getTime() + date.getTimezoneOffset() * -60000);
    }

    static secondsToFulfillmentTimeStr(sec: number) {
        const abs = Math.abs(sec);
        if (abs < 60000) return Math.round(abs / 1000) + '<span class="unit">s</span>';
        else if (abs < 5970000) return Math.round(abs / 60000) + '<span class="unit">m</span>';
        else return '99<span class="unit">+m</span>';
    }

    static weekdayIsNextTo(a: Weekday, b: Weekday): boolean {
        if (a === b) return true;

        switch (a) {
            case Weekday.Monday:
                return b === Weekday.Tuesday;
            case Weekday.Tuesday:
                return b === Weekday.Monday || b === Weekday.Wednesday;
            case Weekday.Wednesday:
                return b === Weekday.Tuesday || b === Weekday.Thursday;
            case Weekday.Thursday:
                return b === Weekday.Wednesday || b === Weekday.Friday;
            case Weekday.Friday:
                return b === Weekday.Thursday || b === Weekday.Saturday;
            case Weekday.Saturday:
                return b === Weekday.Friday || b === Weekday.Sunday;
            case Weekday.Sunday:
                return b === Weekday.Saturday;
        }
    }

    static isToday(date: Date): boolean {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    }

    static getEndOfDay(): Date {
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    }

    static getEndOfDayForDate(date: Date): Date {
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    }

    static addDays(baseDate: Date, daysToAdd: number): Date {
        const luxonDate = LuxonDateTime.fromJSDate(baseDate);
        const resultLuxon = luxonDate.plus({ days: daysToAdd });
        return resultLuxon.toJSDate();
    }
}
